import React from 'react';
import { motion } from 'framer-motion';
import type { MotionValue } from 'framer-motion';
import { showcase } from '../../data/site';
type RacketObjectProps = {
  /** Horizontal position of the reflection sweep, as a percentage string. */
  sweepX: MotionValue<string> | string;
  sweepOpacity: MotionValue<number> | number;
  /** Halo intensity behind the object. */
  haloOpacity: MotionValue<number> | number;
  reduced: boolean;
};

/**
 * The hero object itself. The image is intentionally isolated behind a single
 * constant (`showcase.racketImage`) so it can be swapped for a transparent
 * PNG/WebP cutout later — set `showcase.racketNeedsBlend` to false and the
 * screen blend (which knocks out the black backdrop) is dropped automatically.
 */
export function RacketObject({
  sweepX,
  sweepOpacity,
  haloOpacity,
  reduced
}: RacketObjectProps) {
  return <div className="relative h-full w-full" data-id="element-209" data-mp="3jou7obmxn3iu">
      {/* Neon halo — sits behind the object and grows with the timeline. */}
      <motion.div aria-hidden="true" style={{
      opacity: haloOpacity,
      willChange: 'opacity, transform'
    }} className="pointer-events-none absolute inset-[12%] rounded-full bg-gradient-to-tr from-electric/45 via-grape/35 to-magenta/50 blur-[70px]" data-id="element-210" data-mp="24qe280p4g1cv" />

      {/*
        The source asset still has a black backdrop, so it is knocked out twice:
        the parent stage screen-blends it against the scene, and this radial mask
        feathers the outer edges so no rectangular tile is ever visible.
       */}
      <img src={showcase.racketImage} alt={showcase.racketAlt} loading="lazy" draggable={false} style={showcase.racketNeedsBlend ? {
      WebkitMaskImage: 'radial-gradient(ellipse at 50% 50%, #000 58%, rgba(0,0,0,0.55) 74%, transparent 88%)',
      maskImage: 'radial-gradient(ellipse at 50% 50%, #000 58%, rgba(0,0,0,0.55) 74%, transparent 88%)'
    } : undefined} className="relative h-full w-full select-none object-contain" data-id="element-211" data-mp="2ipdtzh7xkwd9" />

      {/* Reflection sweep travelling across the face of the racket. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" data-id="element-212" data-mp="3jg20vfiz19an">
        <motion.div aria-hidden="true" style={{
        x: reduced ? '140%' : sweepX,
        opacity: reduced ? 0 : sweepOpacity,
        willChange: 'transform, opacity'
      }} className="absolute -inset-y-[30%] left-0 w-[26%] -skew-x-12 bg-gradient-to-r from-transparent via-white/45 to-transparent mix-blend-screen" data-id="element-213" data-mp="3jdxosaax53za" />
      </div>
    </div>;
}